import type { ReactNode } from "react";

/**
 * Dot-path into entity `T`, e.g. "_id", "company.name", "address.country".
 * Resolved at runtime by the renderer; not validated against `T` at compile time.
 */
export type DetailAccessorKey = string;

/**
 * Static flag or predicate. Skips a field or card without branching in page JSX.
 */
export type DetailVisibility<T> = boolean | ((data: T) => boolean);

/**
 * How a read-only detail field is rendered inside a detail card row.
 *
 * - `text` | `date` | `boolean` → standard label/value row (`DetailCardRow`).
 * - `status` → status badge row with optional action dropdown (`DetailCardStatusRow`).
 * - `link` → anchor opened in a new browser tab.
 * - `custom` → escape hatch when primitives are not enough (use sparingly).
 */
export type DetailFieldDisplayType =
  | "text"
  | "date"
  | "boolean"
  | "status"
  | "link"
  | "email"
  | "custom";

/** Default confirm text when `DetailFieldAction.confirmMessage` is omitted. */
export const DETAIL_ACTION_DEFAULT_CONFIRM_MESSAGE =
  "Are you sure you want to perform this action?";

/** Default placeholder for the status-row action dropdown. */
export const DETAIL_ACTION_DROPDOWN_PLACEHOLDER = "Choose action";

/**
 * One option in the status-row action dropdown.
 * Renderer shows `window.confirm` (or `confirmMessage`) before calling `onClick`.
 */
export interface DetailFieldAction<T> {
  /** Dropdown item label, e.g. "Verify" or "Ban". */
  label: string;

  /** Runs after the user confirms the action dialog. May be async. */
  onClick: (data: T) => void | Promise<void>;

  /**
   * Message shown in the browser confirm dialog before `onClick`.
   * Defaults to {@link DETAIL_ACTION_DEFAULT_CONFIRM_MESSAGE}.
   */
  confirmMessage?: string;

  /**
   * Static or computed disabled state for this dropdown item.
   * Disabled items should not trigger confirm or `onClick`.
   */
  disabled?: boolean | ((data: T) => boolean);

  /**
   * Hide this item instead of showing it disabled.
   * Alternative to `disabled` when the action should not appear at all.
   */
  hidden?: DetailVisibility<T>;
}

/** Shared props for every detail field definition. */
export interface DetailFieldBase<T> {
  /** Left-column label in the card row. */
  label: string;

  /**
   * Dot-path used to read a value from `data`.
   * Omit when `getValue` fully defines the displayed value.
   */
  accessorKey?: DetailAccessorKey;

  /**
   * Custom value resolver. Takes precedence over `accessorKey` when set.
   * Return `undefined`, `null`, or empty string to show "-" in the row.
   */
  getValue?: (data: T) => unknown;

  /** When false, the field is not rendered. */
  hidden?: DetailVisibility<T>;
}

/** Plain text or stringified scalar value. */
export interface DetailTextField<T> extends DetailFieldBase<T> {
  displayType: "text";
}

/**
 * ISO date string (or other parseable date) formatted for display.
 * Renderer uses Luxon; see `format` for the output pattern.
 */
export interface DetailDateField<T> extends DetailFieldBase<T> {
  displayType: "date";

  /**
   * Luxon format string, e.g. "LLLL dd, yyyy HH:mm:ss".
   * Renderer falls back to its app default when omitted.
   */
  format?: string;
}

/**
 * Boolean shown as human-readable text, default "Yes" / "No".
 */
export interface DetailBooleanField<T> extends DetailFieldBase<T> {
  displayType: "boolean";

  /** Overrides default Yes/No labels. */
  mapValue?: {
    true: string;
    false: string;
  };
}

/**
 * Status badge row with an optional action dropdown on the right.
 * Raw value (from `accessorKey` or `getValue`) is passed to the shared `Status`
 * component unless `mapValue` transforms it first.
 */
export interface DetailStatusField<T> extends DetailFieldBase<T> {
  displayType: "status";

  /**
   * Maps raw data values to Status-compatible tokens, e.g.
   * `{ true: "enabled", false: "disabled" }` for boolean flags.
   */
  mapValue?: Record<string | number, string>;

  /**
   * Dropdown actions shown on the right side of the status row.
   * Omit or leave empty for read-only status rows.
   */
  actions?: DetailFieldAction<T>[];

  /**
   * Hides the entire action dropdown (e.g. `canManage` / `canManageStatus` flags).
   * Prefer this over repeating `hidden` on every action.
   */
  actionsHidden?: DetailVisibility<T>;

  /**
   * Placeholder when no action has been chosen yet.
   * Defaults to {@link DETAIL_ACTION_DROPDOWN_PLACEHOLDER}.
   */
  actionsPlaceholder?: string;
}

/**
 * Link opened in a new browser tab (`target="_blank"`, `rel="noopener noreferrer"`).
 * Use for internal routes, external URLs, Sumsub, file downloads, etc.
 */
export interface DetailLinkField<T> extends DetailFieldBase<T> {
  displayType: "link";

  /**
   * Full `href` for the anchor. Required.
   * Return `undefined` to show "-" (no link).
   */
  getHref: (data: T) => string | undefined;

  /**
   * Anchor text. Defaults to the resolved field value (accessorKey / getValue).
   */
  getLabel?: (data: T) => string | undefined;
}

/**
 * Email address rendered as a `mailto:` link (no `target="_blank"`).
 * Use for any email field: publicEmail, createdBy.email, etc.
 */
export interface DetailEmailField<T> extends DetailFieldBase<T> {
  displayType: "email";
}

/**
 * Escape hatch for fields that cannot be expressed with other display types.
 * Promote repeated patterns to a new `displayType` instead of adding more `custom` fields.
 */
export interface DetailCustomField<T> extends DetailFieldBase<T> {
  displayType: "custom";

  /** Arbitrary React output for the right column. */
  render: (data: T) => ReactNode;
}

/** Discriminated union of all supported read-only detail field configs. */
export type DetailFieldConfig<T> =
  | DetailTextField<T>
  | DetailDateField<T>
  | DetailBooleanField<T>
  | DetailStatusField<T>
  | DetailLinkField<T>
  | DetailEmailField<T>
  | DetailCustomField<T>;

/**
 * Reusable list of fields spread into a card's `fields` array at build time.
 *
 * Example:
 * `fields: [...baseFields, ...identifierFieldsFor(data.identificationType)]`
 */
export type DetailFieldGroup<T> = DetailFieldConfig<T>[];

/**
 * Variant field groups keyed by a discriminator (e.g. bank account identification type).
 *
 * Example:
 * `const rows = IDENTIFIER_FIELD_GROUPS[data.identificationType] ?? IDENTIFIER_FIELD_GROUPS.default`
 */
export type DetailFieldGroupMap<T, K extends string | number> = Record<
  K,
  DetailFieldGroup<T>
>;

/** One card inside `DetailCardsGrid`. */
export interface DetailCardConfig<T> {
  /** Card header title, e.g. "Company overview". */
  title: string;

  /** Rows rendered inside the card body. */
  fields: DetailFieldConfig<T>[];

  /** When false, the entire card is not rendered. */
  hidden?: DetailVisibility<T>;

  /** Shows an "Edit" button in the card header. Pair with `onEdit`. */
  editable?: boolean;

  /**
   * Header Edit click handler. Page opens the appropriate edit modal
   * (e.g. `EditCompanyCoreDetailsModal` section).
   */
  onEdit?: () => void;
}

/** Root config for a details panel: a grid of cards. */
export interface DetailCardsConfig<T> {
  cards: DetailCardConfig<T>[];
}
